import { useState, useCallback, useMemo, Suspense, lazy } from "react";
import { track } from "@/lib/tracking";
import type { LeadInput } from "@/lib/schemas";
import { SectionSkeleton } from "@/components/ui-sr";
import { SEO } from "@/components/SEO";
import { buildServiceLD } from "@/lib/seo";
import { siteConfig } from "@/site.config";

// Above-the-fold (eager)
import {
  SiteHeader,
  Hero,
  SimulatorMobile,
  SocialProofTicker,
  SixVerticalsGrid,
} from "@/components/sections";

// Below-the-fold (lazy + Suspense) — import por ARQUIVO (não pelo barrel) p/
// splitar cada seção em seu próprio chunk e reduzir o JS inicial no mobile.
const HowItWorks = lazy(() =>
  import("@/components/sections/HowItWorks").then(m => ({
    default: m.HowItWorks,
  }))
);
const Benefits = lazy(() =>
  import("@/components/sections/Benefits").then(m => ({ default: m.Benefits }))
);
const TrioBazilioGrid = lazy(() =>
  import("@/components/sections/TrioBazilioGrid").then(m => ({
    default: m.TrioBazilioGrid,
  }))
);
const FAQ = lazy(() =>
  import("@/components/sections/FAQ").then(m => ({ default: m.FAQ }))
);
const FinalCTA = lazy(() =>
  import("@/components/sections/FinalCTA").then(m => ({ default: m.FinalCTA }))
);
// LeadDialog (radix Dialog + react-hook-form + zod) só carrega no 1º clique de CTA
const LeadDialog = lazy(() =>
  import("@/components/marketing/LeadDialog").then(m => ({
    default: m.LeadDialog,
  }))
);

type LeadDialogState = {
  open: boolean;
  vertical?: LeadInput["vertical"];
  valor?: number;
  source: string;
};

/**
 * Home Consórcio Bazilio — hub das 6 ALPs verticais + trio de consultores.
 */
export default function Home() {
  const [dialogReady, setDialogReady] = useState(false);
  const [leadDialog, setLeadDialog] = useState<LeadDialogState>({
    open: false,
    source: "home",
  });

  const openLead = useCallback(
    (source: string, vertical?: LeadInput["vertical"], valor?: number) => {
      track("lead_dialog_opened", { source, vertical, valor });
      setDialogReady(true);
      setLeadDialog({ open: true, vertical, valor, source });
    },
    []
  );

  const homeJsonLd = useMemo(() => {
    const base = siteConfig.seo.canonicalBase;
    return [
      buildServiceLD({
        serviceType: "Consórcio de Veículos",
        name: "Consórcio de Veículos Bazilio",
        description:
          "Cartas de crédito de R$ 30 mil a R$ 200 mil para automóveis 0km e seminovos. Sem juros, sem entrada.",
        url: `${base}/consorcio/veiculos`,
      }),
      buildServiceLD({
        serviceType: "Consórcio de Motos",
        name: "Consórcio de Motos Bazilio",
        description:
          "Cartas de R$ 8 mil a R$ 60 mil. De 125cc a big trail. Sem juros, sem entrada.",
        url: `${base}/consorcio/motos`,
      }),
      buildServiceLD({
        serviceType: "Consórcio de Imóveis",
        name: "Consórcio de Imóveis Bazilio",
        description:
          "Crédito imobiliário de R$ 100 mil a R$ 1,5 milhão. Use FGTS como lance.",
        url: `${base}/consorcio/imoveis`,
      }),
      buildServiceLD({
        serviceType: "Consórcio Agro",
        name: "Consórcio Agro Bazilio",
        description:
          "Crédito rural até R$ 1,2 milhão para máquinas, terras, insumos e benfeitorias.",
        url: `${base}/consorcio/agro`,
      }),
      buildServiceLD({
        serviceType: "Consórcio de Pesados",
        name: "Consórcio de Caminhões e Pesados Bazilio",
        description:
          "Cartas de R$ 100 mil a R$ 800 mil para caminhões, ônibus e implementos rodoviários.",
        url: `${base}/consorcio/pesados`,
      }),
      buildServiceLD({
        serviceType: "Consórcio de Serviços",
        name: "Consórcio de Serviços Bazilio",
        description:
          "Reforma, viagem, faculdade, casamento, estética. Cartas de R$ 3 mil a R$ 80 mil.",
        url: `${base}/consorcio/servicos`,
      }),
    ];
  }, []);

  return (
    <>
      <SEO jsonLd={homeJsonLd} />
      <SiteHeader />

      <main id="main-content" tabIndex={-1}>
        <Hero openLead={openLead} />
        <SimulatorMobile openLead={openLead} />
        <SocialProofTicker />
        <SixVerticalsGrid />

        <Suspense fallback={<SectionSkeleton variant="text" tone="white" />}>
          <HowItWorks />
        </Suspense>
        <Suspense fallback={<SectionSkeleton variant="text" tone="white" />}>
          <Benefits />
        </Suspense>
        <Suspense fallback={<SectionSkeleton variant="split" tone="cream" />}>
          <TrioBazilioGrid />
        </Suspense>
        <Suspense fallback={<SectionSkeleton variant="text" tone="white" />}>
          <FAQ openLead={openLead} />
        </Suspense>
        <Suspense fallback={<SectionSkeleton variant="compact" tone="navy" />}>
          <FinalCTA openLead={openLead} />
        </Suspense>
      </main>

      {dialogReady && (
        <Suspense fallback={null}>
          <LeadDialog
            open={leadDialog.open}
            onOpenChange={o => setLeadDialog(prev => ({ ...prev, open: o }))}
            source={leadDialog.source}
            initialVertical={leadDialog.vertical}
            initialValor={leadDialog.valor}
            title="Quero simular meu consórcio"
            description="Deixe seus dados e a equipe Bazilio fala com você pelo WhatsApp."
          />
        </Suspense>
      )}
    </>
  );
}
